import React from 'react';
import {
  CloseOutlined,
  DollarOutlined,
  SafetyCertificateOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import { Button } from 'antd';
import { useSelector } from 'react-redux';

import { SESSION_STATUSES, selectors, utils } from 'state';
import { isSimplifiedOptionSettlementFailed } from '../../../../state/app/simplified_options/presentation';

import { ordinaryTradeIsDisabled, sessionBlocksTrading } from '../model';
import type { ExecutionIntent } from '../executionModel';
import { executionIntentKey } from '../executionModel';
import CashExchangeForm from './CashExchangeForm';
import ODAForm from './ODAForm';
import OrdinaryOrderForm from './OrdinaryOrderForm';
import SimplifiedOptionOrderForm from './SimplifiedOptionOrderForm';

type Props = {
  intent: ExecutionIntent;
  focusedInstrumentId?: number;
  close: () => void;
};

const TITLES = {
  order: 'Order ticket',
  optionOrder: 'Option order',
  oda: 'Order automation',
  exchange: 'Cash exchange',
} as const;

const ExecutionWorkspace: React.FC<Props> = ({
  intent,
  focusedInstrumentId,
  close,
}) => {
  const session = useSelector(selectors.activeSession);
  const assets = useSelector(selectors.underlyingAssets);
  const instruments = useSelector(selectors.financialInstruments);
  const marketHours = useSelector(selectors.marketHours);
  const focusedInstrument = focusedInstrumentId
    ? instruments[focusedInstrumentId]
    : undefined;
  const focusedAsset = focusedInstrument
    ? assets[focusedInstrument.underlying_asset]
    : undefined;
  const targetInstrument =
    'instrumentId' in intent ? instruments[intent.instrumentId] : undefined;
  const targetAsset = targetInstrument
    ? assets[targetInstrument.underlying_asset]
    : undefined;
  const tradingBlocked = sessionBlocksTrading(session.status);
  const invalidTarget =
    'instrumentId' in intent && (!targetInstrument || !targetAsset);
  const targetClosedFromInterval = targetAsset
    ? utils.assetIsOpenNew(
        targetAsset,
        session,
        marketHours[targetAsset.market_hours],
      )[1]
    : true;
  const ticketBlocked = (() => {
    if (tradingBlocked || invalidTarget) return true;
    if (intent.kind === 'order' && targetAsset && targetInstrument) {
      return ordinaryTradeIsDisabled({
        status: session.status,
        asset: targetAsset,
        instrument: targetInstrument,
        closedFromInterval: targetClosedFromInterval,
      });
    }
    if (intent.kind === 'optionOrder') {
      const option = targetInstrument?.simplified_option;
      return (
        option?.availability?.available !== true ||
        isSimplifiedOptionSettlementFailed(option)
      );
    }
    if (intent.kind === 'oda') return !targetInstrument?.amount;
    return false;
  })();

  if (intent.kind === 'idle') {
    return (
      <aside
        className="markets-v3-execution is-idle"
        aria-label="Execution desk"
      >
        <div className="markets-v3-execution__head">
          <span>Execution desk</span>
          <h2>Ready when you are</h2>
          <p>
            The ticket area stays available while the complete market universe
            remains visible.
          </p>
        </div>
        <div className="markets-v3-execution__idle">
          {focusedAsset && (
            <div className="markets-v3-execution__focus">
              <span>Chart focus</span>
              <strong>{focusedAsset.name || focusedAsset.ticker}</strong>
              <small>{focusedAsset.ticker}</small>
            </div>
          )}
          <div className="markets-v3-execution__prompt">
            <SwapOutlined />
            <span>
              <strong>Trade</strong>
              Open an order ticket from any live quote.
            </span>
          </div>
          <div className="markets-v3-execution__prompt">
            <SafetyCertificateOutlined />
            <span>
              <strong>ODA</strong>
              Protect an existing long or short position.
            </span>
          </div>
          <div className="markets-v3-execution__prompt">
            <DollarOutlined />
            <span>
              <strong>Exchange</strong>
              Move cash between the playable wallets.
            </span>
          </div>
          {tradingBlocked && (
            <div className="markets-v3-execution__blocked">
              <i /> {SESSION_STATUSES[session.status]} · tickets become
              available when execution resumes.
            </div>
          )}
        </div>
      </aside>
    );
  }

  const title = TITLES[intent.kind];
  const name =
    intent.kind === 'exchange'
      ? 'Exchange cash'
      : targetAsset?.name || targetAsset?.ticker || 'Unavailable instrument';

  return (
    <aside
      className="markets-v3-execution is-active"
      aria-label="Execution desk"
    >
      <div className="markets-v3-execution__head">
        <div>
          <span>{title}</span>
          <h2>{name}</h2>
          <div className="markets-v3-execution__chips">
            {targetAsset && <b>{targetAsset.ticker}</b>}
            {targetAsset && <b>{targetAsset.resourcetype}</b>}
            <b className={tradingBlocked ? 'is-blocked' : 'is-running'}>
              {SESSION_STATUSES[session.status]}
            </b>
          </div>
        </div>
        <Button
          type="text"
          aria-label="Close ticket"
          icon={<CloseOutlined />}
          onClick={close}
        />
      </div>
      <div
        className="markets-v3-execution__content"
        key={executionIntentKey(intent)}
      >
        {invalidTarget && (
          <div className="markets-v3-execution__missing">
            This instrument is no longer available. Close this ticket and choose
            another market.
          </div>
        )}
        {!invalidTarget && intent.kind === 'order' && (
          <OrdinaryOrderForm
            id={intent.instrumentId}
            onComplete={close}
            tradingBlocked={ticketBlocked}
          />
        )}
        {!invalidTarget && intent.kind === 'optionOrder' && (
          <SimplifiedOptionOrderForm
            id={intent.instrumentId}
            onComplete={close}
            tradingBlocked={ticketBlocked}
          />
        )}
        {!invalidTarget && intent.kind === 'oda' && (
          <ODAForm
            id={intent.instrumentId}
            onComplete={close}
            tradingBlocked={ticketBlocked}
          />
        )}
        {intent.kind === 'exchange' && (
          <CashExchangeForm onComplete={close} tradingBlocked={ticketBlocked} />
        )}
      </div>
    </aside>
  );
};

export default ExecutionWorkspace;
